// src/pages/QuanLyHocSinh/QuanLyHocSinh.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../../context/AuthContext";
import { getHocSinhByLop, addHocSinh, updateHocSinh, deleteHocSinh } from "../../services/hocSinhService";
import { getAllLopHoc, getAllNganh } from "../../services/nguoiDungService";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../config/firebase";
import { useToast } from "../../components/Toast/ToastContext";
import useUnsavedChanges from "../../hooks/useUnsavedChanges";
import UnsavedModal from "../../components/UnsavedModal/UnsavedModal";
import { ROLES } from "../../constants";
import { sanitizeInput, validateTen, validatePhone } from "../../utils/validation";
import { getNamHocActive } from "../../services/namHocService";
import QuanLyHocSinhMobile from "./QuanLyHocSinhMobile";
import QuanLyHocSinhDesktop from "./QuanLyHocSinhDesktop";
import QuanLyHocSinhForm from "./QuanLyHocSinhForm";

const CACHE_KEY = 'quanlyhocsinh_cache';
const CACHE_DURATION = 30 * 60 * 1000;
const loadCache = () => { try { return JSON.parse(localStorage.getItem(CACHE_KEY)) || {}; } catch (e) { return {}; } };
const saveCache = (cache) => { try { localStorage.setItem(CACHE_KEY, JSON.stringify(cache)); } catch (e) {} };

const QuanLyHocSinh = () => {
  const { user, hasRole } = useAuthContext();
  const navigate = useNavigate();
  const { success, error } = useToast();
  const isAdmin = hasRole(ROLES.ADMIN);
  const isTruongNganh = hasRole(ROLES.TRUONG_NGANH) && !isAdmin;
  const isGVCN = hasRole(ROLES.GVCN) && !isAdmin && !isTruongNganh;
  const canAdd = isAdmin || isTruongNganh || isGVCN;
  const canEdit = isAdmin || isTruongNganh || isGVCN;
  const canDelete = isAdmin || isTruongNganh || isGVCN;

  const [hocSinh, setHocSinh] = useState([]);
  const [lopHocList, setLopHocList] = useState([]);
  const [nganhList, setNganhList] = useState([]);
  const [giaoHoList, setGiaoHoList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterLop, setFilterLop] = useState("");
  const [filterNganh, setFilterNganh] = useState("");
  const [filterGioiTinh, setFilterGioiTinh] = useState("");
  const [filterTrangThai, setFilterTrangThai] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const { showConfirm, confirmLeave, confirmStay } = useUnsavedChanges(hasChanges);
  const [confirmDialog, setConfirmDialog] = useState({ show: false, message: '', onConfirm: null, danger: false });
  const [isNhapHocMoi, setIsNhapHocMoi] = useState(false);
  const [tiepTucThem, setTiepTucThem] = useState(false);
  const [namHocActive, setNamHocActive] = useState('2025-2026');
  const [form, setForm] = useState({
    hoTen: "", tenThanh: "", ngaySinh: "", gioiTinh: "Nam",
    lopHienTaiId: "", giaoHoId: "",
    hoTenBo: "", tenThanhBo: "", sdtBo: "",
    hoTenMe: "", tenThanhMe: "", sdtMe: "",
    ngayRuaToi: "", ngayRuocLeLanDau: "", ngayThemSuc: "",
    trangThai: "dangHoc"
  });

  const userLopId = user?.maLopCN || "";
  const userNganhId = user?.maNganh || "";
  const cacheRef = useRef(loadCache());

  // Lấy năm học active
  useEffect(() => {
    const init = async () => {
      try { const nh = await getNamHocActive(); if (nh) setNamHocActive(nh.tenNamHoc || nh.id || '2025-2026'); } catch(e) {}
    };
    init();
  }, []);

  const loadData = useCallback(async (forceReload = false) => {
    const cacheKey = `hocsinh_${user.uid}_${isAdmin ? 'admin' : isTruongNganh ? userNganhId : userLopId}`;
    const cached = cacheRef.current[cacheKey];
    
    if (!forceReload && cached && cached.timestamp > Date.now() - CACHE_DURATION) {
      setHocSinh(cached.hsList || []);
      setLopHocList(cached.lopList || []);
      setNganhList(cached.nganhList || []);
      setGiaoHoList(cached.giaoHoList || []);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [lopData, nganhData, giaoHoSnap] = await Promise.all([
        getAllLopHoc(), getAllNganh(), getDocs(collection(db, "giaoHo"))
      ]);
      const giaoHoData = giaoHoSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setLopHocList(lopData); setNganhList(nganhData); setGiaoHoList(giaoHoData);

      let allHS = [];
      if (isAdmin) {
        for (const lop of lopData) { const hs = await getHocSinhByLop(lop.maLop); allHS.push(...hs); }
      } else if (isTruongNganh) {
        const lopTrongNganh = lopData.filter(l => l.nganhId === userNganhId);
        for (const lop of lopTrongNganh) { const hs = await getHocSinhByLop(lop.maLop); allHS.push(...hs); }
      } else {
        if (userLopId) { allHS = await getHocSinhByLop(userLopId); }
      }
      setHocSinh(allHS);

      cacheRef.current[cacheKey] = { hsList: allHS, lopList: lopData, nganhList: nganhData, giaoHoList: giaoHoData, timestamp: Date.now() };
      saveCache(cacheRef.current);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [isAdmin, isTruongNganh, userNganhId, userLopId, user.uid]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { if (lastUpdate) { loadData(true); } }, [lastUpdate]); // eslint-disable-line

  const availableLop = isAdmin ? lopHocList : isTruongNganh ? lopHocList.filter(l => l.nganhId === userNganhId) : lopHocList.filter(l => l.maLop === userLopId);
  const filterableLop = isAdmin ? lopHocList : isTruongNganh ? lopHocList.filter(l => l.nganhId === userNganhId) : lopHocList.filter(l => l.maLop === userLopId);

  const filteredHocSinh = (() => {
    let result = hocSinh;
    if (searchTerm.trim()) {
      const t = searchTerm.toLowerCase().trim();
      result = result.filter(hs => (hs.hoTen || "").toLowerCase().includes(t) || (hs.tenThanh || "").toLowerCase().includes(t) || (hs.hoTenBo || "").toLowerCase().includes(t) || (hs.hoTenMe || "").toLowerCase().includes(t));
    }
    if (filterLop) result = result.filter(hs => hs.lopHienTaiId === filterLop);
    if (filterNganh) {
      const lopTrongNganh = lopHocList.filter(l => l.nganhId === filterNganh).map(l => l.maLop);
      result = result.filter(hs => lopTrongNganh.includes(hs.lopHienTaiId));
    }
    if (filterGioiTinh) result = result.filter(hs => hs.gioiTinh === filterGioiTinh);
    if (filterTrangThai) result = result.filter(hs => hs.trangThai === filterTrangThai);
    return [...result].sort((a, b) => {
      const dateA = a.ngayCapNhat || a.ngayTao || '';
      const dateB = b.ngayCapNhat || b.ngayTao || '';
      return dateB.localeCompare(dateA);
    });
  })();

  const getTenLop = (ma) => lopHocList.find(l => l.maLop === ma)?.tenLop || ma;
  const getTenGiaoHo = (id) => giaoHoList.find(g => (g.maGiaoHo || g.id) === id)?.tenGiaoHo || id || "—";

  const resetForm = () => {
    setEditingStudent(null);
    setIsNhapHocMoi(false);
    setTiepTucThem(false);
    setForm({ hoTen: "", tenThanh: "", ngaySinh: "", gioiTinh: "Nam", lopHienTaiId: isAdmin || isTruongNganh ? "" : userLopId, giaoHoId: "", hoTenBo: "", tenThanhBo: "", sdtBo: "", hoTenMe: "", tenThanhMe: "", sdtMe: "", ngayRuaToi: "", ngayRuocLeLanDau: "", ngayThemSuc: "", trangThai: "dangHoc" });
    setHasChanges(false);
  };

  const openAddForm = (isNhapHoc = false) => {
    resetForm();
    setIsNhapHocMoi(isNhapHoc);
    
    if (isNhapHoc) {
      // Tự động chọn lớp Chiên Con
      const lopChienCon = availableLop?.find(l => {
        const nganh = nganhList?.find(n => n.maNganh === l.nganhId);
        return l.capLop === 1 && nganh?.thuTu === 1;
      });
      if (lopChienCon) {
        setForm(prev => ({ ...prev, lopHienTaiId: lopChienCon.maLop }));
      }
    }
    
    setShowForm(true);
  };

  const openEditForm = (hs) => {
    setEditingStudent(hs);
    setIsNhapHocMoi(false);
    setForm({ hoTen: hs.hoTen || "", tenThanh: hs.tenThanh || "", ngaySinh: hs.ngaySinh || "", gioiTinh: hs.gioiTinh || "Nam", lopHienTaiId: hs.lopHienTaiId || "", giaoHoId: hs.giaoHoId || "", hoTenBo: hs.hoTenBo || "", tenThanhBo: hs.tenThanhBo || "", sdtBo: hs.sdtBo || "", hoTenMe: hs.hoTenMe || "", tenThanhMe: hs.tenThanhMe || "", sdtMe: hs.sdtMe || "", ngayRuaToi: hs.ngayRuaToi || "", ngayRuocLeLanDau: hs.ngayRuocLeLanDau || "", ngayThemSuc: hs.ngayThemSuc || "", trangThai: hs.trangThai || "dangHoc" });
    setHasChanges(false); setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setHasChanges(false); };
  const updateForm = (field, value) => { setHasChanges(true); setForm(prev => ({ ...prev, [field]: value })); };

  const handleSave = async () => {
    if (!validateTen(form.hoTen)) { error("Họ tên không được để trống!"); return; }
    if (form.sdtBo && !validatePhone(form.sdtBo)) { error("SĐT Bố không hợp lệ!"); return; }
    if (form.sdtMe && !validatePhone(form.sdtMe)) { error("SĐT Mẹ không hợp lệ!"); return; }
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const cleanData = {
        ...form, hoTen: sanitizeInput(form.hoTen), tenThanh: sanitizeInput(form.tenThanh),
        hoTenBo: sanitizeInput(form.hoTenBo), tenThanhBo: sanitizeInput(form.tenThanhBo), sdtBo: sanitizeInput(form.sdtBo),
        hoTenMe: sanitizeInput(form.hoTenMe), tenThanhMe: sanitizeInput(form.tenThanhMe), sdtMe: sanitizeInput(form.sdtMe),
        ngayCapNhat: now
      };

      // Nếu là nhập học mới, append lichSuLop
      if (isNhapHocMoi && !editingStudent) {
        cleanData.lichSuLop = [{
          namHoc: namHocActive,
          lopId: form.lopHienTaiId,
          tenLop: getTenLop(form.lopHienTaiId),
          nganhId: availableLop?.find(l => l.maLop === form.lopHienTaiId)?.nganhId || '',
          lyDo: 'nhapHoc',
          ngay: now
        }];
        cleanData.ngayTao = now;
      }

      if (editingStudent) {
        await updateHocSinh(editingStudent.id, cleanData); success("Cập nhật thành công!");
      } else {
        await addHocSinh(cleanData); success(isNhapHocMoi ? "Nhập học thành công!" : "Thêm học sinh thành công!");
      }

      // Nếu "Thêm tiếp", reset form nhưng giữ lớp
      if (tiepTucThem && isNhapHocMoi) {
        resetForm();
        setIsNhapHocMoi(true);
        const lopChienCon = availableLop?.find(l => {
          const nganh = nganhList?.find(n => n.maNganh === l.nganhId);
          return l.capLop === 1 && nganh?.thuTu === 1;
        });
        if (lopChienCon) {
          setForm(prev => ({ ...prev, lopHienTaiId: lopChienCon.maLop }));
        }
        setTiepTucThem(false);
      } else {
        closeForm();
      }
      setLastUpdate(Date.now());
    } catch (err) { error("Lỗi: " + err.message); }
    finally { setSaving(false); }
  };

  const handleSaveAndLeave = async () => { await handleSave(); if (!saving) confirmLeave(); };

  const handleDelete = (hs) => {
    setConfirmDialog({ show: true, danger: true,
      message: `XÓA "${hs.hoTen}"?\n\n⚠️ Không thể hoàn tác!`,
      onConfirm: async () => {
        try { await deleteHocSinh(hs.id); success("Đã xóa!"); setLastUpdate(Date.now()); }
        catch (err) { error("Lỗi: " + err.message); }
        setConfirmDialog({ show: false, message: '', onConfirm: null, danger: false });
      }
    });
  };

  return (
    <div className="space-y-4 pb-20 md:pb-6">
      <UnsavedModal show={showConfirm} onStay={confirmStay} onLeave={confirmLeave} onSaveAndLeave={handleSaveAndLeave} />
      
      {confirmDialog.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={() => setConfirmDialog({ show: false, message: '', onConfirm: null, danger: false })}>
          <div className="bg-surface rounded-xl p-6 shadow-lg max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4"><span className={`material-symbols-outlined text-[24px] ${confirmDialog.danger ? 'text-error' : 'text-yellow-600'}`}>{confirmDialog.danger ? 'warning' : 'help'}</span><p className="text-body-md whitespace-pre-line">{confirmDialog.message}</p></div>
            <div className="flex gap-2 justify-end"><button onClick={() => setConfirmDialog({ show: false, message: '', onConfirm: null, danger: false })} className="px-4 py-2 bg-surface border border-outline-variant rounded-xl text-body-md font-semibold">Hủy</button><button onClick={confirmDialog.onConfirm} className={`px-4 py-2 rounded-xl text-body-md font-semibold text-white ${confirmDialog.danger ? 'bg-error' : 'bg-primary'}`}>{confirmDialog.danger ? 'Xóa' : 'Xác nhận'}</button></div>
          </div>
        </div>
      )}

      {showForm ? (
        <>
          <div className="flex items-center justify-between">
            <h2 className="text-[24px] md:text-[28px] font-bold text-primary">
              {editingStudent ? '✏️ Sửa học sinh' : isNhapHocMoi ? '🎒 Nhập học mới' : '➕ Thêm học sinh mới'}
            </h2>
            <button 
              onClick={() => hasChanges ? setConfirmDialog({ show: true, message: 'Có thay đổi chưa lưu. Đóng?', danger: false, onConfirm: () => { closeForm(); setConfirmDialog({ show: false, message: '', onConfirm: null, danger: false }); } }) : closeForm()} 
              className="p-2 rounded-full hover:bg-surface-container transition-colors">
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
          <QuanLyHocSinhForm 
            form={form} editingStudent={editingStudent} 
            availableLop={availableLop} giaoHoList={giaoHoList} nganhList={nganhList}
            saving={saving} isAdmin={isAdmin} isTruongNganh={isTruongNganh} 
            isNhapHocMoi={isNhapHocMoi} tiepTucThem={tiepTucThem} setTiepTucThem={setTiepTucThem}
            updateForm={updateForm} onSave={handleSave} onClose={closeForm} 
          />
        </>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-[24px] md:text-[28px] font-bold text-primary">👥 Quản lý học sinh</h2>
              <p className="text-caption text-on-surface-variant">
                {filteredHocSinh.length !== hocSinh.length ? `${filteredHocSinh.length} / ${hocSinh.length} học sinh` : `${hocSinh.length} học sinh`}
              </p>
            </div>
            <div className="hidden md:flex items-center gap-2">
              {(isAdmin || isTruongNganh) && (
                <button onClick={() => openAddForm(true)} className="px-4 py-2.5 bg-secondary-container text-on-secondary-container rounded-full text-body-md font-semibold items-center gap-1.5 hover:bg-secondary/20 transition-colors active:scale-95 flex">
                  <span className="material-symbols-outlined text-[18px]">person_add</span>Nhập học mới
                </button>
              )}
              {canAdd && (
                <button onClick={() => openAddForm(false)} className="px-4 py-2.5 bg-primary text-on-primary rounded-full text-body-md font-semibold items-center gap-1.5 hover:bg-primary/90 transition-colors active:scale-95 flex">
                  <span className="material-symbols-outlined text-[18px]">add</span>Thêm mới
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-outline">search</span>
              <input type="text" placeholder="Tìm kiếm..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full h-12 pl-11 pr-4 bg-surface-container-lowest border border-outline-variant rounded-xl focus:border-primary focus:ring-1 focus:ring-primary outline-none text-body-md" />
              {searchTerm && <button onClick={() => setSearchTerm("")} className="absolute right-3 top-1/2 -translate-y-1/2"><span className="material-symbols-outlined text-[18px]">close</span></button>}
            </div>
            {(isAdmin || isTruongNganh) && (
              <>
                <select value={filterNganh} onChange={e => { setFilterNganh(e.target.value); setFilterLop(""); }} className="hidden md:block bg-surface border border-outline-variant rounded-xl px-3 py-2.5 text-body-md outline-none"><option value="">Ngành</option>{(isTruongNganh ? nganhList.filter(n => n.maNganh === userNganhId) : nganhList).map(n => <option key={n.id} value={n.maNganh}>{n.tenNganh}</option>)}</select>
                <select value={filterLop} onChange={e => setFilterLop(e.target.value)} className="hidden md:block bg-surface border border-outline-variant rounded-xl px-3 py-2.5 text-body-md outline-none"><option value="">Lớp</option>{(filterNganh ? filterableLop.filter(l => l.nganhId === filterNganh) : filterableLop).map(l => <option key={l.id} value={l.maLop}>{l.tenLop}</option>)}</select>
              </>
            )}
            <select value={filterGioiTinh} onChange={e => setFilterGioiTinh(e.target.value)} className="hidden md:block bg-surface border border-outline-variant rounded-xl px-3 py-2.5 text-body-md outline-none"><option value="">Giới tính</option><option value="Nam">Nam</option><option value="Nữ">Nữ</option></select>
            <select value={filterTrangThai} onChange={e => setFilterTrangThai(e.target.value)} className="hidden md:block bg-surface border border-outline-variant rounded-xl px-3 py-2.5 text-body-md outline-none"><option value="">Trạng thái</option><option value="dangHoc">Đang học</option><option value="nghiHoc">Nghỉ học</option><option value="chuyenLop">Chuyển lớp</option><option value="luuBan">Lưu ban</option><option value="totNghiep">Tốt nghiệp</option></select>
          </div>

          <div className="flex gap-2 overflow-x-auto pb-1 md:hidden">
            {[{ value: '', label: 'Tất cả' }, ...(isTruongNganh ? nganhList.filter(n => n.maNganh === userNganhId) : nganhList).map(n => ({ value: n.maNganh, label: n.tenNganh }))].map(c => <button key={c.value} onClick={() => { setFilterNganh(c.value); setFilterLop(''); }} className={`px-4 py-1.5 rounded-full text-caption font-bold flex-shrink-0 ${filterNganh === c.value ? 'bg-secondary-fixed text-on-secondary-fixed-variant' : 'bg-surface-container text-on-surface-variant'}`}>{c.label}</button>)}
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 md:hidden">
            {[{ value: '', label: 'Nam/Nữ' }, { value: 'Nam', label: 'Nam' }, { value: 'Nữ', label: 'Nữ' }].map(c => <button key={c.value} onClick={() => setFilterGioiTinh(c.value)} className={`px-4 py-1.5 rounded-full text-caption font-bold flex-shrink-0 ${filterGioiTinh === c.value ? 'bg-secondary-fixed text-on-secondary-fixed-variant' : 'bg-surface-container text-on-surface-variant'}`}>{c.label}</button>)}
          </div>

          {loading ? <div className="flex items-center justify-center h-64"><span className="material-symbols-outlined text-primary animate-spin text-4xl">sync</span></div> : (
            <>
              <div className="md:hidden"><QuanLyHocSinhMobile filtered={filteredHocSinh} getTenLop={getTenLop} getTenGiaoHo={getTenGiaoHo} onView={(hs) => navigate(`/hoc-sinh/${hs.id}`)} onEdit={openEditForm} onDelete={handleDelete} canEdit={canEdit} canDelete={canDelete} /></div>
              <div className="hidden md:block"><QuanLyHocSinhDesktop filtered={filteredHocSinh} getTenLop={getTenLop} getTenGiaoHo={getTenGiaoHo} onView={(hs) => navigate(`/hoc-sinh/${hs.id}`)} onEdit={openEditForm} onDelete={handleDelete} canEdit={canEdit} canDelete={canDelete} /></div>
            </>
          )}

          {canAdd && <button onClick={() => openAddForm(false)} className="md:hidden fixed bottom-24 right-6 w-14 h-14 bg-primary text-on-primary rounded-2xl shadow-lg flex items-center justify-center active:scale-95 transition-transform z-50"><span className="material-symbols-outlined text-[28px]">add</span></button>}
        </>
      )}
    </div>
  );
};

export default QuanLyHocSinh;